import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "editorContainer",
        "jsonInput",
        "htmlInput",
        "previewFrame",
        "previewLoading",
        "sourceInput"
    ]

    static values = {
        initialHtml: String,
        initialJson: String,
        previewUrl: String,
        canEditHtml: Boolean
    }

    connect() {
        console.log("🔥 [EmailEditor] CONTROLLER CONECTADO COM SUCESSO!");

        if (!this.hasEditorContainerTarget) {
            console.error("❌ [EmailEditor] Target 'editorContainer' NÃO ENCONTRADO no HTML!");
            return;
        }

        console.log("✅ [EmailEditor] Target encontrado! Iniciando GrapesJS...");

        this._resetEditorState()
        this._loadGrapesJSAndInitialize()
        this._boundSaveBeforeSubmit = () => this._forceSaveToInputs()
        this.element.closest("form")?.addEventListener("submit", this._boundSaveBeforeSubmit)
    }

    disconnect() {
        this.element.closest("form")?.removeEventListener("submit", this._boundSaveBeforeSubmit)
        this._cleanupEditor()
    }

    onTabChanged(event) {
        if (event.cancelable) event.preventDefault()

        const targetTab = this._getTargetTab(event)

        if (targetTab === "tab-source" && !this.canEditHtmlValue) {
            console.warn("[EmailEditor] Sem permissão para editar HTML")
            return
        }

        this._switchToTab(targetTab)
        this._handleTabSpecificActions(targetTab)
    }

    onSourceChanged() {
        if (!this.hasSourceInputTarget || !this.hasHtmlInputTarget) return

        const sourceValue = this.sourceInputTarget.value
        if (sourceValue !== undefined && sourceValue !== null) {
            this.htmlInputTarget.value = sourceValue
            if (this.hasJsonInputTarget) this.jsonInputTarget.value = ""
            this.sourceDirty = true
        }
    }

    insertVariable(eventOrString) {
        if (!this.editor) return

        const variable = typeof eventOrString === "string"
            ? eventOrString
            : eventOrString?.currentTarget?.dataset?.value

        if (!variable) return

        const selected = this.editor.getSelected()

        if (selected && selected.is("text")) {
            // Insere dentro de um componente texto selecionado
            selected.append(variable)
        } else {
            // Insere como bloco de tabela compatível com email
            this.editor.addComponents(
                `<table width="100%" cellspacing="0" cellpadding="0" border="0">` +
                `<tr><td style="padding: 10px; font-family: Helvetica, Arial, sans-serif;">${variable}</td></tr>` +
                `</table>`
            )
        }
    }

    _resetEditorState() {
        this.sourceDirty = false
        this.saveTimeout = null

        if (this.editor) this._destroyEditor()

        this.editorContainerTarget.innerHTML = ""
    }

    _loadGrapesJSAndInitialize() {
        this._waitForGrapesJS()
            .then(() => {
                if (this.element.isConnected) this._initEditor()
            })
            .catch((err) => {
                console.error("[EmailEditor]", err)
                this._showError("Erro ao carregar editor visual. Verifique sua conexão.")
            })
    }

    _waitForGrapesJS(maxAttempts = 50) {
        return new Promise((resolve, reject) => {
            if (window.grapesjs) return resolve()

            if (!document.getElementById("grapesjs-css-cdn")) {
                const link = document.createElement("link");
                link.id = "grapesjs-css-cdn";
                link.rel = "stylesheet";
                link.href = "https://cdnjs.cloudflare.com/ajax/libs/grapesjs/0.21.2/css/grapes.min.css";
                document.head.appendChild(link);
            }

            if (!document.getElementById("grapesjs-js-cdn")) {
                const script = document.createElement("script");
                script.id = "grapesjs-js-cdn";
                script.src = "https://cdnjs.cloudflare.com/ajax/libs/grapesjs/0.21.2/grapes.min.js";
                document.head.appendChild(script);
            }

            let attempts = 0
            const check = () => {
                if (window.grapesjs) return resolve()
                if (++attempts >= maxAttempts) return reject(new Error("Timeout carregando GrapesJS da CDN"))
                setTimeout(check, 100)
            }
            check()
        })
    }

    _initEditor() {
        if (!this.element.isConnected) return

        try {
            this.editor = window.grapesjs.init({
                container: this.editorContainerTarget,
                height: "600px",
                width: "auto",
                fromElement: false,
                storageManager: false,
                i18n: this._getI18nConfig(),
                deviceManager: this._getDeviceManagerConfig(),
                styleManager: this._getStyleManagerConfig()
            })

            this._addBlocks()
            this._loadContent()
            this._setupAutoSave()

            setTimeout(() => {
                if (this.editor) this.editor.refresh()
            }, 100)

        } catch (e) {
            console.error("[EmailEditor] Erro ao iniciar:", e)
            this._showError(`Erro ao iniciar editor: ${e.message}`)
        }
    }

    _getI18nConfig() {
        return {
            locale: "pt",
            messages: {
                pt: {
                    styleManager: {
                        empty: "Selecione um elemento para editar",
                        layer: "Camada",
                        fileButton: "Imagens",
                        sectors: {
                            general: "Geral",
                            layout: "Layout",
                            typography: "Tipografia",
                            decorations: "Decorações",
                            extra: "Extra",
                            flex: "Flex",
                            dimension: "Dimensão"
                        },
                        properties: {
                            "font-family": "Fonte",
                            "font-size": "Tamanho",
                            "font-weight": "Peso",
                            "letter-spacing": "Espaçamento",
                            "color": "Cor",
                            "line-height": "Altura da linha",
                            "text-align": "Alinhamento",
                            "text-decoration": "Decoração",
                            "text-shadow": "Sombra",
                            "border": "Borda",
                            "border-radius": "Arredondamento",
                            "background": "Fundo",
                            "background-color": "Cor de fundo",
                            "box-shadow": "Sombra",
                            "width": "Largura",
                            "height": "Altura",
                            "min-width": "Largura mín.",
                            "min-height": "Altura mín.",
                            "max-width": "Largura máx.",
                            "max-height": "Altura máx.",
                            "margin": "Margem",
                            "padding": "Espaçamento",
                            "top": "Topo",
                            "right": "Direita",
                            "bottom": "Inferior",
                            "left": "Esquerda"
                        }
                    },
                    traitManager: {
                        empty: "Selecione um elemento para editar",
                        label: "Configurações",
                        traits: {
                            labels: {id: "ID", alt: "Texto alternativo", title: "Título", href: "Link"}
                        }
                    },
                    panels: {
                        buttons: {
                            titles: {
                                preview: "Visualizar",
                                fullscreen: "Tela cheia",
                                "sw-visibility": "Ver componentes",
                                "export-template": "Exportar",
                                "open-sm": "Estilos",
                                "open-tm": "Configurações",
                                "open-layers": "Camadas",
                                "open-blocks": "Blocos"
                            }
                        }
                    },
                    selectorManager: {
                        label: "Classes",
                        selected: "Selecionado",
                        emptyState: "- Estado -",
                        states: {hover: "Hover", active: "Clique", "nth-of-type(2n)": "Par/Ímpar"}
                    },
                    blockManager: {labels: {}, categories: {}},
                    domComponents: {
                        names: {
                            "": "Caixa", wrapper: "Corpo", text: "Texto", comment: "Comentário",
                            image: "Imagem", video: "Vídeo", label: "Rótulo", link: "Link",
                            map: "Mapa", tfoot: "Rodapé tabela", tbody: "Corpo tabela",
                            thead: "Cabeçalho tabela", table: "Tabela", row: "Linha", cell: "Célula"
                        }
                    },
                    deviceManager: {
                        device: "Dispositivo",
                        devices: {
                            desktop: "Desktop", tablet: "Tablet",
                            mobileLandscape: "Celular horizontal", mobilePortrait: "Celular"
                        }
                    },
                    assetManager: {
                        addButton: "Adicionar imagem",
                        inputPlh: "http://caminho/para/imagem.jpg",
                        modalTitle: "Selecionar imagem",
                        uploadTitle: "Solte arquivos aqui ou clique para enviar"
                    }
                }
            }
        }
    }

    _getDeviceManagerConfig() {
        return {
            devices: [
                {name: "Desktop", width: ""},
                {name: "Tablet", width: "768px"},
                {name: "Mobile", width: "320px", widthMedia: "480px"}
            ]
        }
    }

    _getStyleManagerConfig() {
        return {
            sectors: [
                {
                    name: "Geral",
                    open: true,
                    buildProps: ["width", "padding", "margin", "background-color", "text-align"]
                },
                {
                    name: "Tipografia",
                    open: true,
                    buildProps: ["font-family", "font-size", "font-weight", "color", "line-height"]
                },
                {
                    name: "Borda",
                    open: false,
                    buildProps: ["border", "border-radius"]
                }
            ]
        }
    }

    _addBlocks() {
        if (!this.editor) return
        const bm = this.editor.BlockManager
        this._getBlockDefinitions().forEach(block => bm.add(block.id, block.config))
    }

    _getBlockDefinitions() {
        return [
            {
                id: "text",
                config: {
                    label: "Texto",
                    category: "Básico",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M18.5,4L19.66,8.35L18.7,8.61C18.25,7.74 17.79,6.87 17.26,6.43C16.73,6 16.11,6 15.5,6H13V16.5C13,17 13,17.5 13.33,17.75C13.67,18 14.33,18 15,18V19H9V18C9.67,18 10.33,18 10.67,17.75C11,17.5 11,17 11,16.5V6H8.5C7.89,6 7.27,6 6.74,6.43C6.21,6.87 5.75,7.74 5.3,8.61L4.34,8.35L5.5,4H18.5Z"/></svg>',
                    content: '<p style="padding: 10px;">Insira seu texto aqui...</p>'
                }
            },
            {
                id: "heading",
                config: {
                    label: "Título",
                    category: "Básico",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M3,4H5V10H9V4H11V18H9V12H5V18H3V4M14,18V16H16V6.31L13.5,7.75V5.44L16,4H18V16H20V18H14Z"/></svg>',
                    content: '<h2 style="padding: 10px; margin: 0;">Título da Seção</h2>'
                }
            },
            {
                id: "image",
                config: {
                    label: "Imagem",
                    category: "Básico",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M21,3H3C2,3 1,4 1,5V19A2,2 0 0,0 3,21H21C22,21 23,20 23,19V5C23,4 22,3 21,3M5,17L8.5,12.5L11,15.5L14.5,11L19,17H5Z"/></svg>',
                    content: {type: "image"}
                }
            },
            {
                id: "video",
                config: {
                    label: "Vídeo",
                    category: "Básico",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M17,10.5V7A1,1 0 0,0 16,6H4A1,1 0 0,0 3,7V17A1,1 0 0,0 4,18H16A1,1 0 0,0 17,17V13.5L21,17.5V6.5L17,10.5Z"/></svg>',
                    content: {type: "video"}
                }
            },
            {
                id: "link",
                config: {
                    label: "Link",
                    category: "Básico",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M3.9,12C3.9,10.29 5.29,8.9 7,8.9H11V7H7A5,5 0 0,0 2,12A5,5 0 0,0 7,17H11V15.1H7C5.29,15.1 3.9,13.71 3.9,12M8,13H16V11H8V13M17,7H13V8.9H17C18.71,8.9 20.1,10.29 20.1,12C20.1,13.71 18.71,15.1 17,15.1H13V17H17A5,5 0 0,0 22,12A5,5 0 0,0 17,7Z"/></svg>',
                    content: '<a href="#" style="color: #3b97e3;">Link aqui</a>'
                }
            },
            {
                id: "button",
                config: {
                    label: "Botão",
                    category: "Básico",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M10,16.5L16,12L10,7.5V16.5Z"/></svg>',
                    content: '<a href="#" style="display:inline-block; padding:12px 24px; background:#3b97e3; color:#fff; text-decoration:none; border-radius:4px; font-weight:bold;">Clique Aqui</a>'
                }
            },
            {
                id: "list",
                config: {
                    label: "Lista",
                    category: "Básico",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M7,5H21V7H7V5M7,13V11H21V13H7M4,4.5A1.5,1.5 0 0,1 5.5,6A1.5,1.5 0 0,1 4,7.5A1.5,1.5 0 0,1 2.5,6A1.5,1.5 0 0,1 4,4.5M4,10.5A1.5,1.5 0 0,1 5.5,12A1.5,1.5 0 0,1 4,13.5A1.5,1.5 0 0,1 2.5,12A1.5,1.5 0 0,1 4,10.5M7,19V17H21V19H7M4,16.5A1.5,1.5 0 0,1 5.5,18A1.5,1.5 0 0,1 4,19.5A1.5,1.5 0 0,1 2.5,18A1.5,1.5 0 0,1 4,16.5Z"/></svg>',
                    content: '<ul style="padding-left: 20px;"><li>Item 1</li><li>Item 2</li><li>Item 3</li></ul>'
                }
            },
            {
                id: "quote",
                config: {
                    label: "Citação",
                    category: "Básico",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M14,17H17L19,13V7H13V13H16M6,17H9L11,13V7H5V13H8L6,17Z"/></svg>',
                    content: '<blockquote style="border-left: 4px solid #3b97e3; padding: 15px 20px; margin: 10px 0; font-style: italic; color: #555; background: #f9f9f9;">"Uma citação interessante vai aqui..."</blockquote>'
                }
            },

            // Layout
            {
                id: "section",
                config: {
                    label: "Seção",
                    category: "Layout",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M21,17H3V5H21M21,3H3A2,2 0 0,0 1,5V17A2,2 0 0,0 3,19H8V21H16V19H21A2,2 0 0,0 23,17V5A2,2 0 0,0 21,3Z"/></svg>',
                    content: '<section style="padding:30px; min-height:100px;"></section>'
                }
            },
            {
                id: "container",
                config: {
                    label: "Container",
                    category: "Layout",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3M19,5V19H5V5H19Z"/></svg>',
                    content: '<div style="max-width: 800px; margin: 0 auto; padding: 20px;"></div>'
                }
            },
            {
                id: "columns",
                config: {
                    label: "2 Colunas",
                    category: "Layout",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2 20H22V4H2V20M4 6H11V18H4V6M13 18V6H20V18H13Z"/></svg>',
                    content: '<div style="display:flex; gap:20px;"><div style="flex:1; padding:20px; min-height:100px; background:#f5f5f5;"></div><div style="flex:1; padding:20px; min-height:100px; background:#f5f5f5;"></div></div>'
                }
            },
            {
                id: "columns3",
                config: {
                    label: "3 Colunas",
                    category: "Layout",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2 20H22V4H2V20M9 18H4V6H9V18M15 18H10V6H15V18M20 18H16V6H20V18Z"/></svg>',
                    content: '<div style="display:flex; gap:15px;"><div style="flex:1; padding:15px; min-height:80px; background:#f5f5f5;"></div><div style="flex:1; padding:15px; min-height:80px; background:#f5f5f5;"></div><div style="flex:1; padding:15px; min-height:80px; background:#f5f5f5;"></div></div>'
                }
            },
            {
                id: "columns4",
                config: {
                    label: "4 Colunas",
                    category: "Layout",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2 20H22V4H2V20M7 18H4V6H7V18M12 18H8V6H12V18M17 18H13V6H17V18M20 18H18V6H20V18Z"/></svg>',
                    content: '<div style="display:flex; gap:10px;"><div style="flex:1; padding:10px; min-height:60px; background:#f5f5f5;"></div><div style="flex:1; padding:10px; min-height:60px; background:#f5f5f5;"></div><div style="flex:1; padding:10px; min-height:60px; background:#f5f5f5;"></div><div style="flex:1; padding:10px; min-height:60px; background:#f5f5f5;"></div></div>'
                }
            },
            {
                id: "sidebar",
                config: {
                    label: "Sidebar",
                    category: "Layout",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2 20H22V4H2V20M4 6H8V18H4V6M10 18V6H20V18H10Z"/></svg>',
                    content: '<div style="display:flex; gap:20px;"><div style="width:250px; padding:20px; background:#f5f5f5;"></div><div style="flex:1; padding:20px;"></div></div>'
                }
            },
            {
                id: "divider",
                config: {
                    label: "Divisor",
                    category: "Layout",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M19 13H5V11H19V13Z"/></svg>',
                    content: '<hr style="border:none; border-top:1px solid #ccc; margin:20px 0;">'
                }
            },
            {
                id: "spacer",
                config: {
                    label: "Espaço",
                    category: "Layout",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M8,18H11V15H2V13H22V15H13V18H16L12,22L8,18M12,2L8,6H11V9H2V11H22V9H13V6H16L12,2Z"/></svg>',
                    content: '<div style="height:50px;"></div>'
                }
            },

            {
                id: "form",
                config: {
                    label: "Formulário",
                    category: "Formulário",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M17,7H22V17H17V19A1,1 0 0,0 18,20H20V22H17.5C16.95,22 16,21.55 16,21C16,21.55 15.05,22 14.5,22H12V20H14A1,1 0 0,0 15,19V5A1,1 0 0,0 14,4H12V2H14.5C15.05,2 16,2.45 16,3C16,2.45 16.95,2 17.5,2H20V4H18A1,1 0 0,0 17,5V7M2,7H15V9H4V15H15V17H2V7M6,11H13V13H6V11Z"/></svg>',
                    content: '<form style="padding: 20px;"><div style="margin-bottom: 15px;"><label style="display: block; margin-bottom: 5px; font-weight: bold;">Nome</label><input type="text" placeholder="Seu nome" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px;"></div><div style="margin-bottom: 15px;"><label style="display: block; margin-bottom: 5px; font-weight: bold;">Email</label><input type="email" placeholder="seu@email.com" style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px;"></div><button type="submit" style="padding: 12px 24px; background: #3b97e3; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-weight: bold;">Enviar</button></form>'
                }
            },
            {
                id: "input",
                config: {
                    label: "Campo",
                    category: "Formulário",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M4 15V17H20V15H4M4 11V13H20V11H4M4 7V9H20V7H4Z"/></svg>',
                    content: '<input type="text" placeholder="Digite aqui..." style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px;">'
                }
            },
            {
                id: "textarea",
                config: {
                    label: "Textarea",
                    category: "Formulário",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M5,3C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H5M5,5H19V19H5V5M7,7V9H17V7H7M7,11V13H17V11H7M7,15V17H14V15H7Z"/></svg>',
                    content: '<textarea placeholder="Digite sua mensagem..." style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px; min-height: 100px; resize: vertical;"></textarea>'
                }
            },
            {
                id: "select",
                config: {
                    label: "Select",
                    category: "Formulário",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M7,10L12,15L17,10H7Z"/></svg>',
                    content: '<select style="width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 4px;"><option>Selecione uma opção</option><option>Opção 1</option><option>Opção 2</option><option>Opção 3</option></select>'
                }
            },
            {
                id: "checkbox",
                config: {
                    label: "Checkbox",
                    category: "Formulário",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,5V19H5V5H19M10,17L6,13L7.41,11.58L10,14.17L16.59,7.58L18,9"/></svg>',
                    content: '<label style="display: flex; align-items: center; cursor: pointer;"><input type="checkbox" style="margin-right: 8px;"> Aceito os termos</label>'
                }
            },

            // Componentes
            {
                id: "card",
                config: {
                    label: "Card",
                    category: "Componentes",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M19,5V19H5V5H19M19,3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M13.96,12.29L11.21,15.83L9.25,13.47L6.5,17H17.5L13.96,12.29Z"/></svg>',
                    content: '<div style="border: 1px solid #ddd; border-radius: 8px; overflow: hidden; max-width: 350px;"><img src="https://via.placeholder.com/350x200" style="width: 100%; display: block;"><div style="padding: 20px;"><h3 style="margin: 0 0 10px 0;">Título do Card</h3><p style="color: #666; margin: 0;">Descrição breve do conteúdo do card.</p></div></div>'
                }
            },
            {
                id: "hero",
                config: {
                    label: "Hero",
                    category: "Componentes",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M3,5A2,2 0 0,0 1,7V17A2,2 0 0,0 3,19H21A2,2 0 0,0 23,17V7A2,2 0 0,0 21,5H3M3,7H21V17H3V7M5,9V11H19V9H5M5,13V15H15V13H5Z"/></svg>',
                    content: '<div style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 80px 40px; text-align: center; color: #fff;"><h1 style="margin: 0 0 20px 0; font-size: 2.5em;">Título Principal</h1><p style="margin: 0 0 30px 0; font-size: 1.2em; opacity: 0.9;">Uma descrição impactante para chamar atenção.</p><a href="#" style="display: inline-block; padding: 15px 40px; background: #fff; color: #667eea; text-decoration: none; border-radius: 30px; font-weight: bold;">Saiba Mais</a></div>'
                }
            },
            {
                id: "features",
                config: {
                    label: "Features",
                    category: "Componentes",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12,17.27L18.18,21L16.54,13.97L22,9.24L14.81,8.62L12,2L9.19,8.62L2,9.24L7.45,13.97L5.82,21L12,17.27Z"/></svg>',
                    content: '<div style="display: flex; gap: 30px; padding: 40px;"><div style="flex: 1; text-align: center;"><div style="width: 60px; height: 60px; background: #3b97e3; border-radius: 50%; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 24px;">★</div><h3 style="margin: 0 0 10px 0;">Feature 1</h3><p style="color: #666; margin: 0;">Descrição da feature.</p></div><div style="flex: 1; text-align: center;"><div style="width: 60px; height: 60px; background: #3b97e3; border-radius: 50%; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 24px;">★</div><h3 style="margin: 0 0 10px 0;">Feature 2</h3><p style="color: #666; margin: 0;">Descrição da feature.</p></div><div style="flex: 1; text-align: center;"><div style="width: 60px; height: 60px; background: #3b97e3; border-radius: 50%; margin: 0 auto 15px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 24px;">★</div><h3 style="margin: 0 0 10px 0;">Feature 3</h3><p style="color: #666; margin: 0;">Descrição da feature.</p></div></div>'
                }
            },
            {
                id: "testimonial",
                config: {
                    label: "Depoimento",
                    category: "Componentes",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,8.39C13.57,9.4 14.5,10.38 14.5,12C14.5,13.38 13.38,14.5 12,14.5C10.62,14.5 9.5,13.38 9.5,12C9.5,10.38 10.43,9.4 12,8.39M12,4A8,8 0 0,1 20,12C20,14.13 19.17,16.04 17.8,17.4L15.6,15.2C16.5,14.4 17,13.3 17,12C17,9.58 15.03,7.73 12,6.46V4M12,20A8,8 0 0,1 4,12C4,9.87 4.83,7.96 6.2,6.6L8.4,8.8C7.5,9.6 7,10.7 7,12C7,14.42 8.97,16.27 12,17.54V20Z"/></svg>',
                    content: '<div style="background: #f9f9f9; padding: 30px; border-radius: 8px; text-align: center; max-width: 500px; margin: 0 auto;"><img src="https://via.placeholder.com/80" style="width: 80px; height: 80px; border-radius: 50%; margin-bottom: 15px;"><p style="font-style: italic; color: #555; margin: 0 0 15px 0;">"Este é um depoimento incrível de um cliente satisfeito."</p><strong style="color: #333;">Nome do Cliente</strong><br><small style="color: #888;">Cargo, Empresa</small></div>'
                }
            },
            {
                id: "cta",
                config: {
                    label: "CTA",
                    category: "Componentes",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4M12,10.5A1.5,1.5 0 0,1 13.5,12A1.5,1.5 0 0,1 12,13.5A1.5,1.5 0 0,1 10.5,12A1.5,1.5 0 0,1 12,10.5M7.5,10.5A1.5,1.5 0 0,1 9,12A1.5,1.5 0 0,1 7.5,13.5A1.5,1.5 0 0,1 6,12A1.5,1.5 0 0,1 7.5,10.5M16.5,10.5A1.5,1.5 0 0,1 18,12A1.5,1.5 0 0,1 16.5,13.5A1.5,1.5 0 0,1 15,12A1.5,1.5 0 0,1 16.5,10.5Z"/></svg>',
                    content: '<div style="background: #3b97e3; padding: 50px 40px; text-align: center;"><h2 style="color: #fff; margin: 0 0 15px 0;">Pronto para começar?</h2><p style="color: rgba(255,255,255,0.9); margin: 0 0 25px 0;">Entre em contato conosco hoje mesmo!</p><a href="#" style="display: inline-block; padding: 15px 40px; background: #fff; color: #3b97e3; text-decoration: none; border-radius: 4px; font-weight: bold;">Fale Conosco</a></div>'
                }
            },
            {
                id: "footer",
                config: {
                    label: "Rodapé",
                    category: "Componentes",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M5,3C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3H5M5,5H19V19H5V5M7,17H17V15H7V17Z"/></svg>',
                    content: '<footer style="background: #333; color: #fff; padding: 40px; text-align: center;"><p style="margin: 0 0 15px 0;">© 2024 Sua Empresa. Todos os direitos reservados.</p><div style="display: flex; justify-content: center; gap: 20px;"><a href="#" style="color: #fff; text-decoration: none;">Sobre</a><a href="#" style="color: #fff; text-decoration: none;">Contato</a><a href="#" style="color: #fff; text-decoration: none;">Privacidade</a></div></footer>'
                }
            },
            {
                id: "map",
                config: {
                    label: "Mapa",
                    category: "Componentes",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M15,19L9,16.89V5L15,7.11M20.5,3C20.44,3 20.39,3 20.34,3L15,5.1L9,3L3.36,4.9C3.15,4.97 3,5.15 3,5.38V20.5A0.5,0.5 0 0,0 3.5,21C3.55,21 3.61,21 3.66,20.97L9,18.9L15,21L20.64,19.1C20.85,19 21,18.85 21,18.62V3.5A0.5,0.5 0 0,0 20.5,3Z"/></svg>',
                    content: {type: "map"}
                }
            },

            {
                id: "email-header",
                config: {
                    label: "Header Email",
                    category: "Email",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M20,4H4A2,2 0 0,0 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6A2,2 0 0,0 20,4M20,18H4V8L12,13L20,8V18M20,6L12,11L4,6V6H20V6Z"/></svg>',
                    content: '<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #3b97e3;"><tr><td style="padding: 20px; text-align: center;"><img src="https://via.placeholder.com/150x50/ffffff/3b97e3?text=LOGO" alt="Logo" style="max-width: 150px;"></td></tr></table>'
                }
            },
            {
                id: "email-button",
                config: {
                    label: "Botão Email",
                    category: "Email",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M10,16.5L16,12L10,7.5V16.5Z"/></svg>',
                    content: '<table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" style="padding: 20px;"><table cellpadding="0" cellspacing="0" border="0"><tr><td style="background-color: #3b97e3; border-radius: 5px;"><a href="#" style="display: inline-block; padding: 15px 30px; font-family: Helvetica, Arial, sans-serif; font-size: 16px; color: #ffffff; text-decoration: none; font-weight: bold;">CLIQUE AQUI</a></td></tr></table></td></tr></table>'
                }
            },
            {
                id: "email-footer",
                config: {
                    label: "Footer Email",
                    category: "Email",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M20,4H4A2,2 0 0,0 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6A2,2 0 0,0 20,4M20,18H4V8L12,13L20,8V18M20,6L12,11L4,6V6H20V6Z"/></svg>',
                    content: '<table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f5f5f5;"><tr><td style="padding: 20px; text-align: center; font-family: Helvetica, Arial, sans-serif; font-size: 12px; color: #666;"><p style="margin: 0 0 10px 0;">© 2024 Sua Empresa. Todos os direitos reservados.</p><p style="margin: 0;"><a href="#" style="color: #3b97e3; text-decoration: none;">Descadastrar</a></p></td></tr></table>'
                }
            },
            {
                id: "email-table",
                config: {
                    label: "Tabela Email",
                    category: "Email",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M5,4H19A2,2 0 0,1 21,6V18A2,2 0 0,1 19,20H5A2,2 0 0,1 3,18V6A2,2 0 0,1 5,4M5,8V12H11V8H5M13,8V12H19V8H13M5,14V18H11V14H5M13,14V18H19V14H13Z"/></svg>',
                    content: '<table width="100%" cellpadding="10" cellspacing="0" border="1" style="border-collapse: collapse; border-color: #ddd;"><tr style="background: #f5f5f5;"><th>Coluna 1</th><th>Coluna 2</th><th>Coluna 3</th></tr><tr><td>Dado 1</td><td>Dado 2</td><td>Dado 3</td></tr><tr><td>Dado 4</td><td>Dado 5</td><td>Dado 6</td></tr></table>'
                }
            },
            {
                id: "email-2cols",
                config: {
                    label: "2 Colunas Email",
                    category: "Email",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M2 20H22V4H2V20M4 6H11V18H4V6M13 18V6H20V18H13Z"/></svg>',
                    content: '<table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td width="50%" valign="top" style="padding: 20px;"><p>Conteúdo da coluna esquerda</p></td><td width="50%" valign="top" style="padding: 20px;"><p>Conteúdo da coluna direita</p></td></tr></table>'
                }
            },
            {
                id: "email-image",
                config: {
                    label: "Imagem Email",
                    category: "Email",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M21,3H3C2,3 1,4 1,5V19A2,2 0 0,0 3,21H21C22,21 23,20 23,19V5C23,4 22,3 21,3M5,17L8.5,12.5L11,15.5L14.5,11L19,17H5Z"/></svg>',
                    content: '<table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td align="center" style="padding: 20px;"><img src="https://via.placeholder.com/600x300" alt="Imagem" style="max-width: 100%; height: auto; display: block;"></td></tr></table>'
                }
            },
            {
                id: "email-spacer",
                config: {
                    label: "Espaço Email",
                    category: "Email",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M8,18H11V15H2V13H22V15H13V18H16L12,22L8,18M12,2L8,6H11V9H2V11H22V9H13V6H16L12,2Z"/></svg>',
                    content: '<table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="height: 30px; line-height: 30px;">&nbsp;</td></tr></table>'
                }
            },
            {
                id: "email-divider",
                config: {
                    label: "Divisor Email",
                    category: "Email",
                    media: '<svg viewBox="0 0 24 24"><path fill="currentColor" d="M19 13H5V11H19V13Z"/></svg>',
                    content: '<table width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="padding: 20px 0;"><hr style="border: none; border-top: 1px solid #ddd; margin: 0;"></td></tr></table>'
                }
            }
        ]
    }

    _loadContent() {
        if (!this.editor) return

        let json = this.initialJsonValue
        const html = this.initialHtmlValue

        if (json === "null" || json === '""' || json === '{}' || json === "undefined") json = null

        if (json && json.trim().length > 5) {
            try {
                let parsed = JSON.parse(json)

                while (typeof parsed === 'string') {
                    parsed = JSON.parse(parsed)
                }

                if (parsed && typeof parsed === 'object') {
                    const hasPages = Array.isArray(parsed.pages) && parsed.pages.length > 0
                    const hasComponents = Array.isArray(parsed.components) && parsed.components.length > 0

                    if (hasPages || hasComponents) {
                        this.editor.loadProjectData(parsed)
                        console.log("[EmailEditor] Conteúdo carregado do JSON (projeto completo)")
                        return
                    }
                    console.warn("[EmailEditor] JSON válido mas sem pages/components, usando fallback HTML")
                }
            } catch (e) {
                console.warn("[EmailEditor] JSON inválido, usando fallback HTML:", e)
            }
        }

        if (html && html.trim().length > 0) {
            try {
                const cssMatch = html.match(/<style>([\s\S]*?)<\/style>/i)
                const css = cssMatch ? cssMatch[1] : ''
                const bodyHtml = html
                    .replace(/<style>[\s\S]*?<\/style>\s*/gi, '')
                    .trim()

                if (css) this.editor.setStyle(css)
                if (bodyHtml) this.editor.setComponents(bodyHtml)

                console.log("[EmailEditor] Conteúdo carregado do HTML (fallback)")
            } catch (e) {
                console.error("[EmailEditor] Erro crítico ao carregar HTML:", e)
                this.editor.setComponents("<div>Erro ao carregar layout original.</div>")
            }
        } else {
            console.log("[EmailEditor] Editor iniciado vazio")
        }
    }

    _setupAutoSave() {
        this.editor.on("update", () => this._debouncedSave())
    }

    _debouncedSave() {
        if (this.saveTimeout) clearTimeout(this.saveTimeout)
        this.saveTimeout = setTimeout(() => this._saveToInputs(), 500)
    }

    _saveToInputs() {
        if (!this.editor) return
        if (this.sourceDirty) return
        this._writeEditorToInputs()
    }

    _forceSaveToInputs() {
        if (!this.editor) return
        if (this.sourceDirty) {
            if (this.hasHtmlInputTarget && this.hasSourceInputTarget) {
                this.htmlInputTarget.value = this.sourceInputTarget.value
            }
            return
        }
        this._writeEditorToInputs()
    }

    _writeEditorToInputs() {
        if (this.hasJsonInputTarget) {
            try {
                const projectData = this.editor.getProjectData()
                this.jsonInputTarget.value = JSON.stringify(projectData)
            } catch (e) {
                console.error("[EmailEditor] Erro ao serializar JSON do projeto:", e)
            }
        }

        if (this.hasHtmlInputTarget) {
            const html = this.editor.getHtml()
            const css = this.editor.getCss()
            if (html) {
                this.htmlInputTarget.value = `<style>${css}</style>\n${html}`
            }
        }
    }

    _getTargetTab(event) {
        return event.currentTarget.dataset.tab ||
            event.currentTarget.getAttribute("href")?.replace("#", "")
    }

    _switchToTab(targetTab) {
        this.element.querySelectorAll(".tab-pane").forEach(el => {
            el.style.display = "none"
            el.classList.remove("active")
        })

        this.element.querySelectorAll(".tab-btn").forEach(el => el.classList.remove("active"))

        const activePane = this.element.querySelector(`#${targetTab}`)
        if (activePane) {
            activePane.style.display = "block"
            activePane.classList.add("active")
        }

        const activeBtn = this.element.querySelector(`[data-tab="${targetTab}"]`)
        if (activeBtn) activeBtn.classList.add("active")
    }

    _handleTabSpecificActions(targetTab) {
        if (targetTab === "tab-source") {
            this._saveToInputs()
            if (this.hasSourceInputTarget && this.hasHtmlInputTarget) {
                this.sourceInputTarget.value = this.htmlInputTarget.value
                this.sourceDirty = false
            }

        } else if (targetTab === "tab-editor") {
            if (this.sourceDirty && this.hasHtmlInputTarget) {
                try {
                    const htmlContent = this.htmlInputTarget.value
                    const cssMatch = htmlContent.match(/<style>([\s\S]*?)<\/style>/)
                    const css = cssMatch ? cssMatch[1] : ''
                    const bodyHtml = htmlContent.replace(/<style>[\s\S]*?<\/style>\s*/i, '').trim()

                    if (css) this.editor.setStyle(css)
                    if (bodyHtml) this.editor.setComponents(bodyHtml)

                    if (this.hasJsonInputTarget) this.jsonInputTarget.value = ""
                    this.sourceDirty = false
                } catch (e) {
                    console.warn("[EmailEditor] Erro ao recarregar HTML no editor:", e)
                }
            }
            setTimeout(() => {
                if (this.editor) this.editor.refresh()
            }, 100)

        } else if (targetTab === "tab-preview") {
            this._forceSaveToInputs()
            this._loadPreview()
        }
    }

    async _loadPreview() {
        if (!this.hasPreviewUrlValue || !this.previewUrlValue) return

        this._showPreviewLoading()

        try {
            const htmlContent = await this._fetchPreview()
            this._renderPreview(htmlContent)
        } catch (error) {
            this._showPreviewError(error)
        } finally {
            this._hidePreviewLoading()
        }
    }

    async _fetchPreview() {
        const response = await fetch(this.previewUrlValue, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json",
                "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
            },
            body: JSON.stringify({html: this.htmlInputTarget.value})
        })

        if (!response.ok) throw new Error(`Erro HTTP: ${response.status}`)

        const contentType = response.headers.get("content-type")
        if (contentType?.includes("application/json")) {
            return (await response.json()).html
        }
        return await response.text()
    }

    _renderPreview(htmlContent) {
        this.previewFrameTarget.style.display = "block"
        const doc = this.previewFrameTarget.contentDocument ||
            this.previewFrameTarget.contentWindow.document
        doc.open()
        doc.write(htmlContent)
        doc.close()
    }

    _showPreviewLoading() {
        if (this.hasPreviewLoadingTarget) this.previewLoadingTarget.style.display = "flex"
        if (this.hasPreviewFrameTarget) this.previewFrameTarget.style.display = "none"
    }

    _hidePreviewLoading() {
        if (this.hasPreviewLoadingTarget) this.previewLoadingTarget.style.display = "none"
    }

    _showPreviewError(error) {
        console.error("[EmailEditor] Preview error:", error)
        if (this.hasPreviewFrameTarget) {
            this.previewFrameTarget.srcdoc = `
                <div style="padding: 20px; text-align: center; color: #dc3545; font-family: sans-serif;">
                    <strong>Erro ao carregar pré-visualização:</strong><br>${error.message}
                </div>`
            this.previewFrameTarget.style.display = "block"
        }
    }

    _cleanupEditor() {
        if (this.saveTimeout) clearTimeout(this.saveTimeout)
        if (this.editor) this._destroyEditor()
    }

    _destroyEditor() {
        try {
            this.editor.destroy()
        } catch (e) {
            console.warn("[EmailEditor] Erro ao destruir editor:", e)
        }
        this.editor = null
    }

    _showError(message) {
        if (this.hasEditorContainerTarget) {
            this.editorContainerTarget.innerHTML = `<div class="alert alert-danger m-3">${message}</div>`
        }
    }
}